/**
 * Recovery for the failure that made "Something went wrong" the most common
 * screen in the product.
 *
 * Every route here is a `lazy()` chunk served from `/assets/<name>-<hash>.js`.
 * A deploy rewrites those hashes and Firebase Hosting drops the old files —
 * but the SPA rewrite (`"source": "**"` → `/index.html`) means a request for a
 * chunk that no longer exists does NOT 404. It answers `200 text/html` with the
 * new index.html, and because the `/assets/**` header rule matches on the path
 * rather than on what was served, the browser pins that HTML at the chunk's URL
 * for a year:
 *
 *     $ curl -I https://app.mycityapp.ca/assets/index-DEADBEEF.js
 *     HTTP/2 200
 *     content-type: text/html; charset=utf-8
 *     cache-control: public, max-age=31536000, immutable
 *
 * A module script served as HTML fails MIME checking, so `import()` rejects,
 * and React re-throws that out of `<Suspense>` into the root ErrorBoundary.
 * Every tab that was open when we deployed therefore breaks on its next
 * navigation — which is why the error screen shows up so often — and "Try
 * again" cannot clear it: `React.lazy` caches the rejected promise, so
 * re-rendering re-throws the same error forever. Only a full document load
 * picks up the new index.html and its new chunk names.
 *
 * Firebase now answers a missing asset with a real 404 (the SPA rewrite
 * no longer swallows `/assets/**` — verified against
 * `firebase emulators:start --only hosting`), so this is an honest
 * network failure rather than a JavaScript file that is secretly HTML.
 * The `immutable` header still applies to the 404, though, so a browser
 * may hold that negative for a year and a ROLLBACK — which recreates a
 * content hash that already 404'd — would keep failing. Hence the
 * cache-busting refetch below.
 *
 * So that is what we do: on a module-load failure, reload once. It is cheap
 * (index.html is `no-store`, every unchanged chunk is still in cache) and it
 * lands the reader on the version we just shipped, at the same URL.
 */
import { lazy, type ComponentType } from 'react';
import { logger } from './logger';

/** Time of the last recovery reload. `sessionStorage` survives the reload. */
const RELOAD_STAMP_KEY = 'mycity:chunk-reload-at';

/**
 * A second module-load failure this soon after a recovery reload means the
 * reload did not help, and reloading again would spin. Long enough to cover a
 * slow start on mobile data, short enough that a *later* deploy in the same
 * session is still recovered rather than written off.
 */
const RELOAD_COOLDOWN_MS = 20_000;

/**
 * Browsers word this failure five different ways and not one of them is a
 * typed error class, so matching the message is the only option there is.
 *
 *   Chrome/Edge   TypeError: Failed to fetch dynamically imported module: <url>
 *   Firefox       TypeError: error loading dynamically imported module: <url>
 *   Safari        TypeError: Importing a module script failed.
 *   MIME refusal  Failed to load module script: Expected a JavaScript module
 *                 script but the server responded with a MIME type of "text/html".
 *   Vite preload  Unable to preload CSS for /assets/<name>-<hash>.css
 */
const MODULE_LOAD_FAILURE =
  /dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload|ChunkLoadError|Loading chunk \S+ failed/i;

/** True when `error` is a chunk that could not be fetched or parsed as JS. */
export function isModuleLoadError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return MODULE_LOAD_FAILURE.test(message);
}

/**
 * One indirection so a spec can assert the reload fired: jsdom's `location`
 * cannot be spied on in place.
 */
export const recovery = {
  reload: (): void => window.location.reload(),
  /**
   * Overwrite whatever the HTTP cache is holding for this URL.
   *
   * `/assets/**` is served `immutable, max-age=1y`, and that header
   * applies to the 404 for a chunk a deploy removed just as much as to
   * the file itself. A rollback recreates that exact content hash — a
   * hash is content-addressed, so reverting a commit brings the old
   * filename back — and the browser would answer from its cached
   * negative forever. `cache: 'reload'` forces the network and REPLACES
   * the stored entry, so the reload that follows finds the real file.
   */
  evict: (url: string): Promise<unknown> =>
    fetch(url, { cache: 'reload', credentials: 'same-origin' }),
};

/**
 * The chunk URL out of the browser's error message.
 *
 * Chrome and Firefox name it ("Failed to fetch dynamically imported
 * module: https://…/Home-ABC.js"); Safari does not, and there we simply
 * reload without evicting — which is what this did before, so nothing
 * regresses.
 */
function failedChunkUrl(message: string): string | null {
  return /https?:\/\/\S+?\.(?:js|mjs|css)\b/i.exec(message)?.[0] ?? null;
}

/** Long enough for a conditional GET, short enough not to strand anyone. */
const EVICT_TIMEOUT_MS = 2_000;

function readReloadStamp(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  } catch {
    // Safari private mode throws on every sessionStorage access. Treating
    // that as "never reloaded" is the safe read: the worst case is one
    // extra reload, against a permanently broken screen.
    return 0;
  }
}

function writeReloadStamp(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // See readReloadStamp.
  }
}

/**
 * Decide what to do with a failed chunk load, and act on it.
 *
 * @returns `true` when a reload was started and the caller should stop —
 *          `false` when the error must be let through to the boundary.
 */
export function recoverFromStaleChunk(error: unknown, now: number = Date.now()): boolean {
  if (!isModuleLoadError(error)) return false;

  // Being offline is the other way an `import()` fails, and it is the one
  // case where reloading makes things worse: it swaps a screen the reader
  // can retry from for the browser's own offline page.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    logger.warn('chunk load failed while offline — not reloading');
    return false;
  }

  const message = error instanceof Error ? error.message : String(error);

  if (now - readReloadStamp() < RELOAD_COOLDOWN_MS) {
    logger.error('chunk load failed again after a recovery reload', { message });
    return false;
  }

  writeReloadStamp(now);
  logger.warn('stale chunk after a deploy — reloading onto the shipped version', { message });

  const url = failedChunkUrl(message);
  if (url === null) {
    recovery.reload();
    return true;
  }
  // Evict first, then reload — but never let a hanging request, a missing
  // `fetch`, or a synchronous throw strand the reader on a splash screen.
  // Getting back onto the shipped version matters more than the eviction.
  try {
    void Promise.race([
      recovery.evict(url).catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, EVICT_TIMEOUT_MS)),
    ]).then(() => recovery.reload());
  } catch {
    recovery.reload();
  }
  return true;
}

/**
 * `lazy()` with stale-deploy recovery. Every route should use this instead of
 * `lazy()` directly — see the note at the top of the file.
 */
export function lazyRoute<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy<T>(() =>
    load().catch((error: unknown) => {
      if (recoverFromStaleChunk(error)) {
        // Never settles on purpose: it holds Suspense on the splash while the
        // document is torn down, instead of flashing the error screen on the
        // way out.
        return new Promise<{ default: T }>(() => {
          // Deliberately never settles — the document is on its way out.
        });
      }
      throw error;
    }),
  );
}
